import { default as React, useEffect, useState, FC, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { RouteComponentProps } from 'react-router'

import useDebounce from 'react-use/lib/useDebounce'
import Search from '../../components/Search'
import {
  fetchLocations,
  uploadLocations,
  exportLocations,
} from '../locationsActions'
import { TriggerImportDialog } from '../../components/ImportDialog'
import {
  locationsSelector,
  locationsSearchIndexSelector,
} from '../locationsSelectors'
import useThunkDispatch from '../../common/useThunkDispatch'
import Splash from '../../components/Splash'
import Button from '../../components/Button'
import Pagination from '../../components/Pagination'
import ToggleButtonGroup from '../../components/ToggleButtonGroup'
import {
  ILocation,
  LocationTypes,
  LOCATION_TYPE_PLURAL_LABELS,
} from '../../types/location'
import { IAccount, IBranch, IUser } from '../../types/user'
import withAccountAndBranch from '../../common/withAccountAndBranch'
import BugIcon from '../../icons/bug.svg'
import LocationsRow from './LocationsRow'

const PAGE_SIZE = 10

interface IProps extends RouteComponentProps {
  branch: IBranch
  user: IUser
  account?: IAccount
  isInternal: boolean
}

export const TABLE_WIDTHS = {
  ID: '-fix50',
  LOCATION_CODE: '-w17',
  TYPE: '-w12',
  USER: '-fix150',
  TOTAL: '-fix150 -text-right',
  LAST_CYCLE_COUNT: '-fix150',
  NEXT_CYCLE_COUNT: '-fix150',
  DESCRIPTION: '-g8',
  ACTIONS: '-fix100',
}

type SortKey =
  | 'code'
  | 'locationType'
  | 'user'
  | 'totalValue'
  | 'lastCycleCount'
  | 'nextCycleCountDate'
  | 'description'
type SortDir = 'asc' | 'desc'
type LocationTypeFilter = 'all' | 'truck' | 'bin'

const sortLocations = (
  locations: ILocation[],
  key: SortKey,
  dir: SortDir,
): ILocation[] =>
  [...locations].sort((a, b) => {
    let aVal: any
    let bVal: any

    switch (key) {
      case 'code':
        aVal = a.code.toLowerCase()
        bVal = b.code.toLowerCase()
        break
      case 'locationType':
        aVal = a.locationType.toLowerCase()
        bVal = b.locationType.toLowerCase()
        break
      case 'user':
        aVal = a.user?.name?.toLowerCase() ?? ''
        bVal = b.user?.name?.toLowerCase() ?? ''
        break
      case 'totalValue':
        aVal = a.totalValue ?? 0
        bVal = b.totalValue ?? 0
        break
      case 'lastCycleCount':
        aVal = (a as any).latestCycleCount?.completedAt ?? ''
        bVal = (b as any).latestCycleCount?.completedAt ?? ''
        break
      case 'nextCycleCountDate':
        aVal = a.nextCycleCountDate ?? ''
        bVal = b.nextCycleCountDate ?? ''
        break
      case 'description':
        aVal = a.description?.toLowerCase() ?? ''
        bVal = b.description?.toLowerCase() ?? ''
        break
      default:
        aVal = ''
        bVal = ''
    }

    if (aVal < bVal) return dir === 'asc' ? -1 : 1
    if (aVal > bVal) return dir === 'asc' ? 1 : -1
    return 0
  })

const SortIcon: FC<{ active: boolean; dir: SortDir }> = ({ active, dir }) => (
  <span style={{ marginLeft: 4, opacity: active ? 1 : 0.3, fontSize: 10 }}>
    {active && dir === 'asc' ? '▲' : '▼'}
  </span>
)

const Locations: FC<IProps> = ({
  branch,
  user,
  history,
  account,
  isInternal,
}) => {
  const branchId = branch?.id
  const currentUser = user

  const dispatch = useThunkDispatch()

  const allLocations = useSelector(locationsSelector)
  const locationsIndex = useSelector(locationsSearchIndexSelector)

  const currentAccount = account ?? user.account
  const truckEnabled =
    currentAccount?.truckInventoryFeature && branch?.truckInventoryFeature
  const binEnabled =
    currentAccount?.binInventoryFeature && branch?.binInventoryFeature
  const bothEnabled = truckEnabled && binEnabled

  const [typeFilter, setTypeFilter] = useState<LocationTypeFilter>(
    (history.location.state as any)?.typeFilter || 'all',
  )
  const [sortKey, setSortKey] = useState<SortKey>('code')
  const [sortDir, setSortDir] = useState<SortDir>('asc')
  const [currentPage, setCurrentPage] = useState(
    (history.location.state as any)?.page || 1,
  )
  const [term, setTerm] = useState((history.location.state as any)?.term || '')
  const [loading, setLoading] = useState(false)
  const [filteredLocations, setFilteredLocations] = useState<ILocation[]>([])

  const handleSortClick = (key: SortKey) => {
    setCurrentPage(1)
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  const featureFilteredLocations = useMemo(() => {
    if (truckEnabled && binEnabled) return allLocations
    if (truckEnabled)
      return allLocations.filter((l) => l.locationType === LocationTypes.TRUCK)
    if (binEnabled)
      return allLocations.filter((l) => l.locationType === LocationTypes.BIN)
    return []
  }, [allLocations, truckEnabled, binEnabled])

  const typeFilteredLocations = useMemo(() => {
    if (!bothEnabled || typeFilter === 'all') return featureFilteredLocations
    return featureFilteredLocations.filter((l) => l.locationType === typeFilter)
  }, [featureFilteredLocations, typeFilter, bothEnabled])

  useDebounce(
    () => {
      let base: ILocation[]
      if (term && locationsIndex) {
        base = locationsIndex.search(term).filter((l) => {
          if (!bothEnabled) return true
          if (typeFilter === 'truck')
            return l.locationType === LocationTypes.TRUCK
          if (typeFilter === 'bin') return l.locationType === LocationTypes.BIN
          return true
        })
      } else {
        base = typeFilteredLocations.slice()
      }
      setFilteredLocations(sortLocations(base, sortKey, sortDir))
    },
    300,
    [
      term,
      locationsIndex,
      typeFilteredLocations,
      sortKey,
      sortDir,
      typeFilter,
      bothEnabled,
    ],
  )

  useEffect(() => {
    if (term && locationsIndex) {
      const base = locationsIndex.search(term).filter((l) => {
        if (!bothEnabled) return true
        if (typeFilter === 'truck')
          return l.locationType === LocationTypes.TRUCK
        if (typeFilter === 'bin') return l.locationType === LocationTypes.BIN
        return true
      })
      setFilteredLocations(sortLocations(base, sortKey, sortDir))
    } else {
      setFilteredLocations(
        sortLocations(typeFilteredLocations.slice(), sortKey, sortDir),
      )
    }
  }, [typeFilteredLocations, sortKey, sortDir])

  const pageLocations = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE
    return filteredLocations.slice(start, start + PAGE_SIZE)
  }, [filteredLocations, currentPage])

  const accountId = account?.id ?? currentUser?.account?.id

  const loadLocations = async (branch: IBranch) => {
    const branchId = branch?.id
    if (accountId === undefined || !branchId) return

    setLoading(true)
    try {
      await dispatch(fetchLocations(accountId, branchId))
    } catch (e) {
      console.error('Error loading locations:', e)
    }
    setLoading(false)
  }

  useEffect(() => {
    if (branch) {
      loadLocations(branch)
    }
  }, [branch, account])

  const navigateTo = (path: string) =>
    history.push(path, { typeFilter, term, page: currentPage })
  const locationBase =
    account && branchId ? `/clients/${account.id}/branches/${branchId}` : ''

  if (!branchId) return <Splash isLoading />

  const nothingFound = !filteredLocations.length && !loading

  const SortableHeader: FC<{
    label: string
    sortId: SortKey
    className: string
  }> = ({ label, sortId, className }) => (
    <div
      className={`table_td table_title ${className}`}
      onClick={() => handleSortClick(sortId)}
      style={{ cursor: 'pointer', userSelect: 'none' }}
    >
      {label}
      <SortIcon active={sortKey === sortId} dir={sortDir} />
    </div>
  )

  return (
    <Splash
      isLoading={loading}
      withWrapper
      className="container locations-layout"
    >
      <div className="filter">
        {bothEnabled && (
          <>
            <div className="filter_col">
              <ToggleButtonGroup<LocationTypeFilter>
                options={['all', 'bin', 'truck']}
                value={typeFilter}
                renderOption={(t) => LOCATION_TYPE_PLURAL_LABELS[t] ?? 'All'}
                onChange={(t) => {
                  setTypeFilter(t)
                  setCurrentPage(1)
                }}
              />
            </div>
            <div className="filter_sep" />
          </>
        )}
        <div className="filter_col -large">
          <Search
            placeholder="Search by code or user name"
            value={term}
            onChange={(e) => {
              setTerm(e.target.value)
              setCurrentPage(1)
            }}
          />
        </div>
        <div className="filter_sep" />
        <div className="filter_col">
          <Button
            onClick={() =>
              navigateTo(
                locationBase
                  ? `${locationBase}/locations-add`
                  : '/locations-add',
              )
            }
            className="form_control -size"
          >
            Add Location
          </Button>
        </div>
        {isInternal && (
          <>
            <div className="filter_sep" />
            <div className="filter_col">
              <TriggerImportDialog
                action={(file) => uploadLocations(file, accountId!, branchId!)}
                templateName="import_locations_template.csv"
                templateUrl="/import_locations_template.csv"
                title="Import locations"
                triggerElement={<Button block>Import / Export</Button>}
                exportAction={async () => {
                  await dispatch(
                    exportLocations(accountId!, branchId!, branch?.name),
                  )
                }}
                errorTitle="Some records failed to import"
              />
            </div>
          </>
        )}
      </div>

      {nothingFound ? (
        <div className="replenishment-empty" style={{ minHeight: '500px' }}>
          <div className="replenishment-empty_title">Nothing here</div>
          <div className="replenishment-empty_message">
            New Locations will appear here
          </div>
          <BugIcon className="replenishment-empty_icon" />
        </div>
      ) : (
        <>
          <div className="locations-layout_table">
            <div className="table">
              <div className="table_head-tr">
                <SortableHeader
                  label="Location Code"
                  sortId="code"
                  className={TABLE_WIDTHS.LOCATION_CODE}
                />
                {bothEnabled && (
                  <SortableHeader
                    label="Type"
                    sortId="locationType"
                    className={TABLE_WIDTHS.TYPE}
                  />
                )}
                <SortableHeader
                  label="User"
                  sortId="user"
                  className={TABLE_WIDTHS.USER}
                />
                <SortableHeader
                  label="Total $"
                  sortId="totalValue"
                  className={TABLE_WIDTHS.TOTAL}
                />
                <SortableHeader
                  label="Last Cycle Count"
                  sortId="lastCycleCount"
                  className={TABLE_WIDTHS.LAST_CYCLE_COUNT}
                />
                <SortableHeader
                  label="Next Count"
                  sortId="nextCycleCountDate"
                  className={TABLE_WIDTHS.NEXT_CYCLE_COUNT}
                />
                <SortableHeader
                  label="Description"
                  sortId="description"
                  className={TABLE_WIDTHS.DESCRIPTION}
                />
                <div
                  className={`table_td table_title ${TABLE_WIDTHS.ACTIONS}`}
                />
              </div>
              {pageLocations.map((location) => (
                <LocationsRow
                  key={location.id}
                  location={location}
                  showTypeColumn={!!bothEnabled}
                  onClick={(loc) =>
                    navigateTo(
                      locationBase
                        ? `${locationBase}/locations/${loc.id}`
                        : `/locations/${loc.id}`,
                    )
                  }
                  onEdit={(loc) =>
                    navigateTo(
                      locationBase
                        ? `${locationBase}/locations/${loc.id}/edit`
                        : `/locations/${loc.id}/edit`,
                    )
                  }
                />
              ))}
            </div>
          </div>
          <Pagination
            currentPage={currentPage}
            totalItems={filteredLocations.length}
            pageSize={PAGE_SIZE}
            onPageChange={setCurrentPage}
          />
        </>
      )}
      <Splash isLoading={loading} bgColor="#F2F6FA" />
    </Splash>
  )
}

export default withAccountAndBranch(Locations)
